/**
 * The Team Member's handlers for ObjectiveConfigurationDialog on their own objectives
 * (userRole "employee"). My Objectives uses them, and so does the year-end check-in's step for
 * next year's objectives (HR UAT list #27), so a Team Member's objectives are made the same way
 * in both: as their own, not their People Leader's, for the year on screen and dated inside it,
 * with the meeting about them that opens their approval.
 *
 * The People Leader's side of the same dialog is useObjectiveConfigHandlers.
 */
import { useCallback } from 'react'
import toast from 'react-hot-toast'
import { performanceService } from '@/services/api/performanceService'
import { datesForYear } from '@/lib/objectiveYear'

export interface OwnObjectiveUser {
  id?: string
  employeeId?: string
  displayName?: string
  managerId?: string
  managerEmployeeId?: string
  jobTitle?: string
}

export interface UseOwnObjectiveHandlersOptions {
  user: OwnObjectiveUser | null | undefined
  /** The year whose objectives are on screen: objectives are added for it, and its meeting
   *  opens approvals. */
  year: number
  /** Reload the objectives on screen. */
  onChange: () => Promise<void>
  /** Reload the meetings about them, after one may have been set up. */
  onSessionChange?: () => Promise<void>
}

export function useOwnObjectiveHandlers({ user, year, onChange, onSessionChange }: UseOwnObjectiveHandlersOptions) {
  const ownId = user?.employeeId || user?.id || ''

  // The meeting with the People Leader about the objectives on screen: for the year on screen,
  // so next year's objectives get next year's meeting (held in this year's year-end check-in
  // when there is one: HR UAT list #27). The backend returns the open one if it exists.
  const ensureObjectiveSession = useCallback(async () => {
    if (!user?.managerId) return
    try {
      await performanceService.createCollaborativeSession({
        employee_id: user.employeeId || '',
        employee_name: user.displayName || 'Team Member',
        employee_role: user.jobTitle || '',
        manager_id: user.managerEmployeeId || user.managerId,
        manager_name: 'People Leader',
        session_type: 'objective_revision',
        performance_year: year,
        scheduled_by: 'system'
      })
    } catch (sessionError) {
      if (import.meta.env.DEV) console.log('Note: Collaborative session may already exist', sessionError)
    }
    await onSessionChange?.()
  }, [user, year, onSessionChange])

  const handleApprove = useCallback(async (objectiveId: number, feedback?: string, edits?: any) => {
    try {
      await performanceService.approveObjective(objectiveId, {
        feedback,
        edits: edits || undefined
      })
      await onChange()
      toast.success('Objective approved successfully')
    } catch (err) {
      console.error('Failed to approve objective:', err)
      toast.error('Failed to approve objective')
      throw err
    }
  }, [onChange])

  const handleReject = useCallback(async (objectiveId: number, reason: string) => {
    try {
      await performanceService.rejectObjective(objectiveId, { reason })
      await onChange()
      toast.success('Objective rejected successfully')
    } catch (err) {
      console.error('Failed to reject objective:', err)
      toast.error('Failed to reject objective')
      throw err
    }
  }, [onChange])

  const handleUpdate = useCallback(async (objectiveId: number, updates: any, employeeId?: string) => {
    try {
      const saved = await performanceService.updateEmployeeObjective(
        employeeId || ownId,
        objectiveId,
        updates
      )

      // Create collaborative session for manager to review changes —
      // only when the save sent the objective for approval. An
      // unchanged objective stays active and needs no revision.
      if (saved?.status === 'pending_approval') {
        await ensureObjectiveSession()
      }

      await onChange()
    } catch (err) {
      console.error('Failed to update objective:', err)
      throw err
    }
  }, [ownId, ensureObjectiveSession, onChange])

  const handleAllocate = useCallback(async (objectives: any[]) => {
    try {
      for (const obj of objectives) {
        const strategicObjectiveId = obj.strategicObjectiveId ?? obj.strategic_objective_id ?? null
        const strategicInitiativeId = obj.strategicInitiativeId ?? obj.strategic_initiative_id ?? null
        const operationalInitiativeId = obj.operationalInitiativeId ?? obj.operational_initiative_id ?? null

        // For the year on screen, dated inside it (HR UAT list #27)
        const { startDate, endDate } = datesForYear(year, obj.startDate, obj.endDate)
        const objectiveData: Record<string, any> = {
          title: obj.title,
          description: obj.description,
          kpa_id: obj.kpa,
          start_date: `${startDate}T00:00:00`,
          end_date: `${endDate}T23:59:59`,
          performance_year: year,
          kpis: (obj.kpis || []).map((kpi: any) => ({
            name: kpi.title,
            description: kpi.description,
            target: kpi.target,
            unit: kpi.unit,
            frequency: kpi.frequency,
            weight: kpi.weight
          }))
        }

        if (strategicObjectiveId) objectiveData.strategic_objective_id = strategicObjectiveId
        if (strategicInitiativeId) objectiveData.strategic_initiative_id = strategicInitiativeId
        if (operationalInitiativeId) objectiveData.operational_initiative_id = operationalInitiativeId

        await performanceService.createEmployeeObjective(ownId, objectiveData as any)
      }

      await ensureObjectiveSession()

      await onChange()
      toast.success(`${objectives.length} objective(s) created successfully`)
    } catch (err) {
      console.error('Failed to create objectives:', err)
      toast.error('Failed to create objectives')
      throw err
    }
  }, [ownId, year, ensureObjectiveSession, onChange])

  // Objectives brought in from last year or the Objective Library (the copy route made them)
  const handleObjectivesAdded = useCallback(async () => {
    await ensureObjectiveSession()
    await onChange()
  }, [ensureObjectiveSession, onChange])

  return { ensureObjectiveSession, handleApprove, handleReject, handleUpdate, handleAllocate, handleObjectivesAdded }
}
